import { getAuth, GoogleAuthProvider, signInWithPopup, fetchSignInMethodsForEmail, signInWithEmailAndPassword, createUserWithEmailAndPassword, UserCredential, signInWithCredential, getAdditionalUserInfo, linkWithCredential, EmailAuthProvider, linkWithPopup, sendEmailVerification } from 'firebase/auth';

import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { normalizeEmailClient, getOrCreateUserByEmailClient } from './canonicalClient';
import { isCanonicalAuthEnabled } from '../../config/env';
import { savePendingGoogleCredential } from '../../utils/authLinking';
import { getEmailUidMappingMode, type MappingMode } from '../../config/env';
import { logAuthLifecycle } from '../logging/secureLogger';

// One bounded retry for transient network failures — IDEMPOTENT credential
// calls only (plain sign-ins; never link/create, whose first request can land
// server-side even when the response is lost). The very first identitytoolkit
// request from a fresh browser profile (e.g. an incognito window) is the
// classic victim: the cold connection fails once and an immediate retry
// succeeds (2026-08-19 first-attempt sign-in failure).
async function withOneNetworkRetry<T>(attempt: () => Promise<T>): Promise<T> {
	try {
		return await attempt();
	} catch (e: any) {
		if (e?.code !== 'auth/network-request-failed') throw e;
		await new Promise((resolve) => setTimeout(resolve, 500));
		return attempt();
	}
}

// Sign-in error codes are the diagnosis trail for "it failed for some reason"
// reports — record the code (never the email/password) on every error branch.
function logSignInError(flow: string, code: unknown): void {
	try { logAuthLifecycle('signin_error', null, { flow, code: String(code || 'UNKNOWN') }); } catch {}
}

/**
 * Branded verification email (task #50): the server generates the action link
 * and sends the gold prest0 template from prest0n@prest0.ai, landing on our
 * /auth/action page. Fire-and-forget and NEVER blocking — signup proceeds
 * regardless. Falls back to Firebase's plain sendEmailVerification if the
 * callable is unreachable, so a verification email of SOME kind always goes
 * out.
 *
 * THE single client entry point for requesting a verification email
 * (2026-08-14 consolidation): Signup, Wallet, and EmailVerifyBanner all call
 * this — the raw sendEmailVerification lives ONLY inside this helper as its
 * fallback. Never rejects.
 */
export async function requestBrandedVerificationEmail(user: { emailVerified?: boolean } | null): Promise<void> {
	try {
		const { getFunctions, httpsCallable } = await import('firebase/functions');
		const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'authSendVerificationEmail');
		const continuePath = typeof window !== 'undefined' ? window.location.pathname + window.location.search : undefined;
		const res: any = await fn({ continue: continuePath });
		if (res?.data?.sent === false && res?.data?.reason !== 'already-verified' && res?.data?.reason !== 'throttled') {
			throw new Error(res?.data?.reason || 'branded verification not sent');
		}
	} catch (err) {
		console.warn('[auth] branded verification email unavailable — falling back to Firebase default:', err);
		try {
			const current = getAuth().currentUser;
			if (current && !current.emailVerified) await sendEmailVerification(current);
		} catch (ve) {
			console.warn('[auth] sendEmailVerification fallback failed:', ve);
		}
	}
}

// Types for callable interop
// Canonical mapping flow supersedes legacy ensure/resolve types

export type SignInOutcomeOk = {
	status: 'ok';
	uid: string;
	canonicalUid: string;
	conflict: boolean;
	newUser: boolean;
};

export type SignInOutcomeLinkRequired = {
	status: 'link_required';
	emailPreview: string; // first 2 chars only
	methods: string[];
	message: string;
};

export type SignInOutcomeError = {
	status: 'error';
	code: string;
	message: string;
};

export type SignInOutcome = SignInOutcomeOk | SignInOutcomeLinkRequired | SignInOutcomeError;

// Canonical system uses a single server call; client fallbacks removed

// No-op: do not create or touch users/{uid} on sign-in
async function ensureUserDocExists(_uid: string): Promise<void> { return; }

// Mapping modes removed; canonical enforced

export async function signInWithGoogleAndResolveUser(): Promise<SignInOutcome> {
	const auth = getAuth();
	const provider = new GoogleAuthProvider();
	try {
		// If currently anonymous, link Google to the anonymous user to avoid creating a second auth user
		let result: UserCredential;
		if (auth.currentUser && (auth.currentUser as any).isAnonymous) {
			result = await linkWithPopup(auth.currentUser, provider);
		} else {
			result = await signInWithPopup(auth, provider);
		}
		const user = result.user;
		const email = user.email || '';
		if (!email) {
			await auth.signOut().catch(() => {});
			return { status: 'error', code: 'INVALID_EMAIL', message: 'Email not available from provider. Please select an account with a visible email.' };
		}
		const normalized = normalizeEmailClient(email);
		let canonical: any = { canonicalUid: user.uid, ownerUid: user.uid, normalizedEmail: normalized, emailHash: '', conflict: false, providers: [] };
		if (isCanonicalAuthEnabled()) {
			try {
				canonical = await getOrCreateUserByEmailClient(normalized, 'google.com');
			} catch (e) {
				console.warn('[auth] canonical getOrCreateUserByEmail failed; proceeding without mapping', e);
			}
		}
		if (canonical.conflict && canonical.canonicalUid !== user.uid) {
			// Save pending Google credential for later linking but proceed as signed-in
			try {
				const cred = GoogleAuthProvider.credentialFromResult(result);
				const idToken = cred?.idToken;
				if (idToken) savePendingGoogleCredential(idToken);
			} catch {}
			await ensureUserDocExists(user.uid);
			try { localStorage.removeItem('user_data'); } catch {}
			try {
				window.dispatchEvent(new CustomEvent('app:data_bootstrap', { detail: { uid: (canonical.ownerUid || canonical.canonicalUid) } }));
			} catch {}
			const addl = getAdditionalUserInfo(result);
			const isNewUser = !!addl?.isNewUser;
			return { status: 'ok', uid: user.uid, canonicalUid: canonical.canonicalUid || user.uid, conflict: true, newUser: isNewUser };
		}
		// Ensure user doc for the current Firebase UID
		await ensureUserDocExists(user.uid);
		try { localStorage.removeItem('user_data'); } catch {}
		try {
			// Broadcast to force consumers to reload state from users/{ownerUid}
			window.dispatchEvent(new CustomEvent('app:data_bootstrap', { detail: { uid: (canonical.ownerUid || canonical.canonicalUid) } }));
		} catch {}
		const addl = getAdditionalUserInfo(result);
		const isNewUser = !!addl?.isNewUser;
		return { status: 'ok', uid: user.uid, canonicalUid: user.uid, conflict: false, newUser: isNewUser };
	} catch (err: any) {
		// Handle provider mismatch pre-signin
		if (err?.code === 'auth/account-exists-with-different-credential') {
			const email = String(err?.customData?.email || '');
			const normalized = email ? normalizeEmailClient(email) : '';
			try {
				const cred = GoogleAuthProvider.credentialFromError(err);
				const idToken = cred?.idToken;
				if (idToken) savePendingGoogleCredential(idToken);
			} catch {}
			let methods: string[] = [];
			try { methods = email ? await fetchSignInMethodsForEmail(getAuth(), email) : []; } catch {}
			return {
				status: 'link_required',
				emailPreview: normalized.slice(0, 2),
				methods,
				message: 'This email is already registered with a different sign-in method. Please sign in with that method to link your Google account.',
			};
		}
		const code = String(err?.code || 'UNKNOWN');
		let message = 'Google sign-in failed. Please try again.';
		if (code === 'auth/operation-not-allowed') message = 'Google provider is not enabled in Firebase Auth.';
		else if (code === 'auth/unauthorized-domain') message = 'This domain is not authorized in Firebase Auth settings.';
		else if (code === 'auth/invalid-credential') message = 'Invalid Google credential. Check the Web Client ID and OAuth origins.';
		else if (code === 'auth/popup-blocked' || code === 'auth/popup-closed-by-user') message = 'Sign-in popup was blocked or closed. Please allow popups and try again.';
		return { status: 'error', code, message };
	}
}

export async function signInWithGoogleIdTokenAndResolveUser(idToken: string): Promise<SignInOutcome> {
    const auth = getAuth();
    console.log('🔐 signInWithGoogleIdTokenAndResolveUser: Starting...');
    try {
        if (!idToken) {
            console.error('🔐 signInWithGoogleIdTokenAndResolveUser: No ID token provided');
            return { status: 'error', code: 'INVALID_GOOGLE_TOKEN', message: 'Invalid Google session. Please try again.' };
        }
        console.log('🔐 signInWithGoogleIdTokenAndResolveUser: Creating credential from ID token');
        const credential = GoogleAuthProvider.credential(idToken);
        // If currently anonymous, link Google to the anonymous user to avoid creating a second auth user
        let result: UserCredential;
        if (auth.currentUser && (auth.currentUser as any).isAnonymous) {
            console.log('🔐 signInWithGoogleIdTokenAndResolveUser: Linking to anonymous user');
            try {
                result = await linkWithCredential(auth.currentUser, credential);
            } catch (linkErr: any) {
                // An EXISTING Google account can never be linked onto a leftover
                // (legacy) anonymous session — without this fallback that was a
                // permanent dead end: generic 'Google sign-in failed' on every
                // attempt until site data was cleared. The user asked to sign
                // in, so sign them into their real account instead.
                if (linkErr?.code === 'auth/credential-already-in-use' || linkErr?.code === 'auth/email-already-in-use') {
                    console.warn('🔐 signInWithGoogleIdTokenAndResolveUser: link blocked (existing account) — signing in directly');
                    result = await withOneNetworkRetry(() => signInWithCredential(auth, credential));
                } else {
                    throw linkErr;
                }
            }
        } else {
            console.log('🔐 signInWithGoogleIdTokenAndResolveUser: Signing in with credential');
            result = await withOneNetworkRetry(() => signInWithCredential(auth, credential));
        }
        const user = result.user;
        console.log('🔐 signInWithGoogleIdTokenAndResolveUser: Firebase sign-in successful, UID:', user.uid);
        const email = user.email || '';
        if (!email) {
            console.error('🔐 signInWithGoogleIdTokenAndResolveUser: No email from provider');
            await auth.signOut().catch(() => {});
            return { status: 'error', code: 'INVALID_EMAIL', message: 'Email not available from provider. Please select an account with a visible email.' };
        }
        console.log('🔐 signInWithGoogleIdTokenAndResolveUser: Email:', email);
        const normalized = normalizeEmailClient(email);
        let canonical: any = { canonicalUid: user.uid, ownerUid: user.uid, normalizedEmail: normalized, emailHash: '', conflict: false, providers: [] };
        if (isCanonicalAuthEnabled()) {
            console.log('🔐 signInWithGoogleIdTokenAndResolveUser: Calling canonical mapping...');
            try {
                canonical = await getOrCreateUserByEmailClient(normalized, 'google.com');
                console.log('🔐 signInWithGoogleIdTokenAndResolveUser: Canonical result:', canonical);
            } catch (e) {
                console.warn('[auth] canonical getOrCreateUserByEmail failed; proceeding without mapping', e);
            }
        }
        if (canonical.conflict && canonical.canonicalUid !== user.uid) {
            // Proceed as success; emit bootstrap with canonical UID
            console.log('🔐 signInWithGoogleIdTokenAndResolveUser: Conflict detected, using canonical UID:', canonical.canonicalUid);
            try { localStorage.removeItem('user_data'); } catch {}
            try { window.dispatchEvent(new CustomEvent('app:data_bootstrap', { detail: { uid: (canonical.ownerUid || canonical.canonicalUid) } })); } catch {}
            const addl = getAdditionalUserInfo(result);
            const isNewUser = !!addl?.isNewUser;
            return { status: 'ok', uid: user.uid, canonicalUid: canonical.canonicalUid || user.uid, conflict: true, newUser: isNewUser };
        }
        await ensureUserDocExists(user.uid);
        try { localStorage.removeItem('user_data'); } catch {}
        try { window.dispatchEvent(new CustomEvent('app:data_bootstrap', { detail: { uid: (canonical.ownerUid || canonical.canonicalUid) } })); } catch {}
        const addl = getAdditionalUserInfo(result);
        const isNewUser = !!addl?.isNewUser;
        console.log('🔐 signInWithGoogleIdTokenAndResolveUser: Success! Returning ok status');
        return { status: 'ok', uid: user.uid, canonicalUid: user.uid, conflict: false, newUser: isNewUser };
    } catch (err: any) {
        console.error('🔐 signInWithGoogleIdTokenAndResolveUser: Error:', err);
        const code = String(err?.code || 'UNKNOWN');
        logSignInError('google-idtoken', code);
        let message = 'Google sign-in failed. Please try again.';
        if (code === 'auth/operation-not-allowed') message = 'Google provider is not enabled in Firebase Auth.';
        else if (code === 'auth/unauthorized-domain') message = 'This domain is not authorized in Firebase Auth settings.';
        else if (code === 'auth/invalid-credential') message = 'Invalid Google credential. Check the Web Client ID and OAuth origins.';
        else if (code === 'auth/popup-blocked' || code === 'auth/popup-closed-by-user') message = 'Sign-in popup was blocked or closed. Please allow popups and try again.';
        else if (code === 'auth/network-request-failed') message = 'Network hiccup — please check your connection and try again.';
        return { status: 'error', code, message };
	}
}

export async function signInWithEmailPasswordAndResolveUser(
    email: string,
    password: string,
    options?: { createMode?: boolean }
): Promise<SignInOutcome> {
	const auth = getAuth();
	console.log('🔍 EMAIL SIGN-IN START:', { email, mappingMode: getEmailUidMappingMode() });
	try {
		const normalized = normalizeEmailClient(email);

		// Create-account mode: check for existing methods first, then create
		if (options?.createMode) {
			console.log('🧭 Create-account mode enabled. Checking existing methods for:', normalized);
			const methodsPre = await fetchSignInMethodsForEmail(auth, normalized).catch(() => [] as string[]);
			console.log('🔎 Methods for email in create-mode:', methodsPre);
			if (methodsPre.length > 0) {
				return {
					status: 'link_required',
					emailPreview: normalized.slice(0, 2),
					methods: methodsPre,
					message: 'This email is already registered. Please sign in with the listed method or reset your password.',
				};
			}
			// No existing methods — proceed to create
			let createdCred: UserCredential | null = null;
			if (auth.currentUser && (auth.currentUser as any).isAnonymous) {
				try {
					const emailCred = EmailAuthProvider.credential(normalized, password);
					createdCred = await linkWithCredential(auth.currentUser, emailCred);
					console.log('🔗 Anonymous account upgraded to email/password, UID:', createdCred.user.uid);
				} catch (le: any) {
					if (le?.code === 'auth/credential-already-in-use' || le?.code === 'auth/email-already-in-use') {
						const methods2 = await fetchSignInMethodsForEmail(auth, normalized).catch(() => [] as string[]);
						return {
							status: 'link_required',
							emailPreview: normalized.slice(0, 2),
							methods: methods2,
							message: methods2.length > 0
								? 'This email is already registered. Please sign in with the listed method to link your email/password.'
								: 'This email is already registered — log in with your usual method, or reset your password.',
						};
					}
					throw le;
				}
			} else {
				try {
					createdCred = await createUserWithEmailAndPassword(auth, normalized, password);
				} catch (createErr: any) {
					if (createErr?.code === 'auth/email-already-in-use') {
						// fetchSignInMethodsForEmail can return [] even for existing accounts (Firebase deprecated it).
						// Gracefully handle: tell user the email exists and to log in instead.
						console.warn('🔁 Email already in use despite fetchSignInMethods returning []. Prompting login.');
						return {
							status: 'link_required',
							emailPreview: normalized.slice(0, 2),
							methods: ['password'],
							message: 'This email is already registered. Please log in instead, or reset your password.',
						};
					}
					throw createErr;
				}
			}
			console.log('✅ New account created (create-mode), UID:', createdCred.user.uid);

			// Branded verification email — fire-and-forget, never blocks signup.
			void requestBrandedVerificationEmail(createdCred.user);

			// Continue with canonical mapping using created credentials
			const userCreated = createdCred.user;
			const userEmailCreated = userCreated.email || normalized;
			let canonicalCreated: any = { canonicalUid: userCreated.uid, ownerUid: userCreated.uid, normalizedEmail: normalizeEmailClient(userEmailCreated), emailHash: '', conflict: false, providers: [] };
			if (isCanonicalAuthEnabled()) {
				try { canonicalCreated = await getOrCreateUserByEmailClient(userEmailCreated, 'password'); } catch (e) { console.warn('[auth] canonical getOrCreateUserByEmail failed; proceeding without mapping', e); }
			}
			await ensureUserDocExists(userCreated.uid);
			try { localStorage.removeItem('user_data'); } catch {}
			try { window.dispatchEvent(new CustomEvent('app:data_bootstrap', { detail: { uid: (canonicalCreated.ownerUid || canonicalCreated.canonicalUid) } })); } catch {}
			const addlNew = getAdditionalUserInfo(createdCred);
			const isNewUserNew = !!addlNew?.isNewUser;
			return { status: 'ok', uid: userCreated.uid, canonicalUid: userCreated.uid, conflict: false, newUser: isNewUserNew };
		}

		// Default (non-create mode): sign-in ONLY. Signing in with an address
		// that has no account must NEVER silently create one (pre-2026-08-17
		// behavior): a typo'd or not-yet-provisioned email minted a shadow
		// Firebase user under whatever password was typed — and, from an
		// anonymous session, LINKED that email to the anonymous uid, poisoning
		// the address for its real owner (INCIDENT 2026-08-17, org-invite
		// sign-in lockout). Account creation is exclusively the explicit
		// create-account mode above.
		let cred: UserCredential | null = null;
		try {
			console.log('📧 Attempting sign-in for:', normalized);
			cred = await withOneNetworkRetry(() => signInWithEmailAndPassword(auth, normalized, password));
			console.log('✅ Sign-in successful, UID:', cred.user.uid);
		} catch (e: any) {
			console.log('❌ Sign-in failed:', e?.code);
			logSignInError('password', e?.code);
			if (e?.code === 'auth/user-not-found') {
				// Other providers may still hold this address — tell the user
				// which, instead of creating anything.
				const methods = await fetchSignInMethodsForEmail(auth, normalized).catch(() => [] as string[]);
				console.log('🔎 Existing methods for email:', methods);
				if (methods.length > 0) {
					return {
						status: 'link_required',
						emailPreview: normalized.slice(0, 2),
						methods,
						message: 'This email is already registered with a different method. Please use that method and link your email/password.',
					};
				}
				return {
					status: 'error',
					code: 'auth/user-not-found',
					message: 'No account exists for this email. Create an account to continue.',
				};
			} else if (e?.code === 'auth/wrong-password' || e?.code === 'auth/invalid-credential') {
				// In create-account mode, surface that the email already exists and show supported methods
				if (options?.createMode) {
					const methods = await fetchSignInMethodsForEmail(auth, normalized).catch(() => [] as string[]);
					return {
						status: 'link_required',
						emailPreview: normalized.slice(0, 2),
						methods,
						message: methods.length > 0
							? 'This email is already registered. Please sign in with the listed method or reset your password.'
							: 'This email is already registered — log in with your usual method, or reset your password.',
					};
				}
				return { status: 'error', code: e.code, message: 'Incorrect email or password.' };
			} else if (e?.code === 'auth/too-many-requests') {
				return { status: 'error', code: e.code, message: 'Too many attempts. Please try again later.' };
			} else if (e?.code === 'auth/email-already-in-use') {
				const methods = await fetchSignInMethodsForEmail(auth, normalized).catch(() => [] as string[]);
				return {
					status: 'link_required',
					emailPreview: normalized.slice(0, 2),
					methods,
					// Enumeration protection can leave methods empty even for
					// existing accounts — never point at "the listed method" then.
					message: methods.length > 0
						? 'This email is already registered. Please use the listed method and then link email/password.'
						: 'This email is already registered — log in with your usual method, or reset your password.',
				};
			} else if (e?.code === 'auth/network-request-failed') {
				return { status: 'error', code: e.code, message: 'Network hiccup — please check your connection and try again.' };
			} else {
				return { status: 'error', code: String(e?.code || 'UNKNOWN'), message: 'Sign-in failed. Please try again.' };
			}
		}

		if (!cred) return { status: 'error', code: 'UNKNOWN', message: 'Sign-in failed. Please try again.' };
		const user = cred.user;
		const userEmail = user.email || normalized;
		if (!userEmail) return { status: 'error', code: 'INVALID_EMAIL', message: 'Email not available.' };
		
		console.log('📍 Calling canonical getOrCreateUserByEmail for:', userEmail);
		let canonical: any = { canonicalUid: user.uid, ownerUid: user.uid, normalizedEmail: normalizeEmailClient(userEmail), emailHash: '', conflict: false, providers: [] };
		if (isCanonicalAuthEnabled()) {
			try {
				canonical = await getOrCreateUserByEmailClient(userEmail, 'password');
			} catch (e) {
				console.warn('[auth] canonical getOrCreateUserByEmail failed; proceeding without mapping', e);
			}
		}
		console.log('📍 Canonical mapping result:', { 
			canonicalUid: canonical.canonicalUid, 
			conflict: canonical.conflict,
			currentUid: user.uid,
			match: canonical.canonicalUid === user.uid
		});
		if (canonical.conflict && canonical.canonicalUid !== user.uid) {
			// The user just proved these credentials to Firebase — NEVER sign
			// them out over mapping drift (pre-2026-08-17 behavior: a stale
			// accounts/{emailHash} doc turned every correct-password sign-in
			// into an immediate signOut + dead-end modal, locking the account
			// out entirely). The server now self-heals stale mappings against
			// Auth; a residual conflict means legacy duplicate accounts, which
			// the Google flows already handle by proceeding under the
			// canonical uid — do the same here.
			console.warn('[auth] canonical mapping conflict — proceeding signed-in under canonical uid', {
				uid: user.uid,
				canonicalUid: canonical.canonicalUid,
			});
			await ensureUserDocExists(user.uid);
			try { localStorage.removeItem('user_data'); } catch {}
			try {
				window.dispatchEvent(new CustomEvent('app:data_bootstrap', { detail: { uid: (canonical.ownerUid || canonical.canonicalUid) } }));
			} catch {}
			const addlConflict = getAdditionalUserInfo(cred);
			return { status: 'ok', uid: user.uid, canonicalUid: canonical.canonicalUid || user.uid, conflict: true, newUser: !!addlConflict?.isNewUser };
		}
		console.log('✅ Using UID:', user.uid);
		await ensureUserDocExists(user.uid);
		try { localStorage.removeItem('user_data'); } catch {}
		try {
			window.dispatchEvent(new CustomEvent('app:data_bootstrap', { detail: { uid: (canonical.ownerUid || canonical.canonicalUid) } }));
		} catch {}
		const addl2 = getAdditionalUserInfo(cred);
		const isNew2 = !!addl2?.isNewUser;
		return { status: 'ok', uid: user.uid, canonicalUid: user.uid, conflict: false, newUser: isNew2 };
	} catch (err: any) {
		console.error('💥 Sign-in error:', err);
		logSignInError('password', err?.code);
		const message = err?.code === 'auth/network-request-failed'
			? 'Network hiccup — please check your connection and try again.'
			: 'Email sign-in failed. Please try again.';
		return { status: 'error', code: String(err?.code || 'UNKNOWN'), message };
	}
}
